import { useCallback } from 'react';
import {
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Input,
  Checkbox,
  RadioGroup,
  Radio,
  HStack,
  VStack,
} from '@chakra-ui/react';

import { SALUTATIONS, SUFFIXES } from '../../constants/driverOptions';
import { PROVINCES } from 'variables/constants';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import { LicenseNumberInput } from 'components/LicenseNumberInput';
import {
  validateLicenseNumber,
  extractOntarioLicenseInfo,
  PROVINCE_IDS,
} from 'utils/licenseNumber';
import type { DriverPersonalInfoProps } from './types';

const getProvinceName = (provinceId: number): string => {
  const province = PROVINCES.find((p) => p.value === provinceId);
  return province?.label || 'Unknown';
};

const DriverPersonalInfo = ({
  driver,
  index,
  quoteProvince,
  applicantDriverId,
  onNestedChange,
}: DriverPersonalInfoProps) => {
  const personalInfo = driver.personal_info;
  const insuranceDetails = driver.insurance_details;
  const { getOptions, getCode, getApplicantRelationCode } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();
  const driverEntity = driver as unknown as Record<string, unknown>;

  const isRequired = (field: string) =>
    isFieldRequired(
      'driver',
      field,
      driver as unknown as Record<string, unknown>,
    );

  const genderOptions = getOptions('applicant_gender');
  const maritalStatusOptions = getOptions('marital_status');

  const allRelationOptions = getOptions('relation_to_applicant');
  const applicantCode = getApplicantRelationCode();
  const anotherDriverIsApplicant =
    applicantDriverId !== null && applicantDriverId !== driver.id;
  const relationOptions = anotherDriverIsApplicant
    ? allRelationOptions.filter((opt) => opt.value !== applicantCode)
    : allRelationOptions;

  const handleSelectChange = (
    field: string,
    value: string,
    answerCodeField?:
      | 'applicant_gender'
      | 'marital_status'
      | 'relation_to_applicant',
  ) => {
    const finalValue = answerCodeField
      ? getCode(value, answerCodeField)
      : value;
    onNestedChange(index, 'personal_info', field, finalValue);
  };

  const handleLicenseNumberChange = useCallback(
    (licenseNumber: string) => {
      onNestedChange(index, 'personal_info', 'license_number', licenseNumber);

      if (quoteProvince === PROVINCE_IDS.ONTARIO) {
        const info = extractOntarioLicenseInfo(licenseNumber);
        if (info) {
          onNestedChange(index, 'licensing', 'date_of_birth', info.dateOfBirth);
          const genderCode = getCode(info.gender, 'applicant_gender');
          onNestedChange(
            index,
            'personal_info',
            'applicant_gender',
            genderCode,
          );
        }
      }
    },
    [index, onNestedChange, quoteProvince, getCode],
  );

  return (
    <>
      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: '80px 1fr 1fr 1fr 80px',
        }}
        gap={2}
        mb={2}
      >
        <GridItem>
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Salutation
            </FormLabel>
            <Select
              variant="main"
              size="sm"
              value={personalInfo.salutation}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'personal_info',
                  'salutation',
                  e.target.value,
                )
              }
              isDisabled={false}
            >
              {SALUTATIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('personal_info.first_name')}
            isInvalid={
              isRequired('personal_info.first_name') &&
              !personalInfo.first_name?.trim()
            }
          >
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              First Name
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={personalInfo.first_name}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'personal_info',
                  'first_name',
                  e.target.value,
                )
              }
              isDisabled={false}
              placeholder="First name"
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Middle Name
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={personalInfo.middle_name}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'personal_info',
                  'middle_name',
                  e.target.value,
                )
              }
              isDisabled={false}
              placeholder="Middle name"
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('personal_info.last_name')}
            isInvalid={
              isRequired('personal_info.last_name') &&
              !personalInfo.last_name?.trim()
            }
          >
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Last Name
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={personalInfo.last_name}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'personal_info',
                  'last_name',
                  e.target.value,
                )
              }
              isDisabled={false}
              placeholder="Last name"
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Suffix
            </FormLabel>
            <Select
              variant="main"
              size="sm"
              value={personalInfo.suffix}
              onChange={(e) =>
                onNestedChange(index, 'personal_info', 'suffix', e.target.value)
              }
              isDisabled={false}
            >
              {SUFFIXES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        }}
        gap={2}
        mb={2}
      >
        <GridItem>
          {(() => {
            const validation = validateLicenseNumber(
              personalInfo.license_number,
              quoteProvince,
              personalInfo.last_name,
            );
            const fieldRequired = isRequired('personal_info.license_number');
            const isEmpty = !personalInfo.license_number?.trim();
            return (
              <FormControl
                isRequired={fieldRequired}
                isInvalid={!validation.valid || (fieldRequired && isEmpty)}
              >
                <FormLabel fontSize="xs" whiteSpace="nowrap">
                  License Number
                </FormLabel>
                <LicenseNumberInput
                  variant="main"
                  size="sm"
                  value={personalInfo.license_number}
                  provinceId={quoteProvince}
                  onChange={handleLicenseNumberChange}
                  isDisabled={false}
                />
                {!validation.valid && (
                  <FormErrorMessage fontSize="xs">
                    {validation.error}
                  </FormErrorMessage>
                )}
              </FormControl>
            );
          })()}
        </GridItem>
        <GridItem>
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Province
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={getProvinceName(quoteProvince)}
              isDisabled
              bg="gray.100"
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('personal_info.applicant_gender')}
            isInvalid={
              isRequired('personal_info.applicant_gender') &&
              !personalInfo.applicant_gender
            }
          >
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Gender
            </FormLabel>
            <Select
              variant="main"
              size="sm"
              value={getCode(personalInfo.applicant_gender, 'applicant_gender')}
              onChange={(e) =>
                handleSelectChange(
                  'applicant_gender',
                  e.target.value,
                  'applicant_gender',
                )
              }
              isDisabled={false}
            >
              {genderOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem>
          <FormControl
            isRequired={isRequired('personal_info.marital_status')}
            isInvalid={
              isRequired('personal_info.marital_status') &&
              !personalInfo.marital_status
            }
          >
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Marital Status
            </FormLabel>
            <Select
              variant="main"
              size="sm"
              value={getCode(personalInfo.marital_status, 'marital_status')}
              onChange={(e) =>
                handleSelectChange(
                  'marital_status',
                  e.target.value,
                  'marital_status',
                )
              }
              isDisabled={false}
            >
              {maritalStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
      </Grid>

      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(2, 1fr)',
        }}
        gap={2}
        alignItems="start"
      >
        <GridItem>
          <VStack spacing={2} align="stretch">
            <FormControl
              isRequired={isRequired('personal_info.relation_to_applicant')}
              isInvalid={
                isRequired('personal_info.relation_to_applicant') &&
                !personalInfo.relation_to_applicant
              }
            >
              <FormLabel fontSize="xs" whiteSpace="nowrap">
                Relation to Applicant
              </FormLabel>
              <Select
                variant="main"
                size="sm"
                value={getCode(
                  personalInfo.relation_to_applicant,
                  'relation_to_applicant',
                )}
                onChange={(e) =>
                  handleSelectChange(
                    'relation_to_applicant',
                    e.target.value,
                    'relation_to_applicant',
                  )
                }
                isDisabled={false}
              >
                {relationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl
              isRequired={isFieldRequired(
                'driver',
                'insurance_details.has_prior_insurance',
                driverEntity,
              )}
            >
              <FormLabel fontSize="xs" whiteSpace="nowrap">
                Previously Insured?
              </FormLabel>
              <RadioGroup
                value={
                  insuranceDetails?.has_prior_insurance === true
                    ? 'yes'
                    : insuranceDetails?.has_prior_insurance === false
                      ? 'no'
                      : ''
                }
                onChange={(value) => {
                  if (value === 'yes') {
                    onNestedChange(
                      index,
                      'insurance_details',
                      'has_prior_insurance',
                      true,
                    );
                  } else {
                    onNestedChange(index, 'insurance_details', {
                      has_prior_insurance: false,
                      continuously_insured_date: null,
                      continuously_insured_years: null,
                      company_insured_date: null,
                      renewal_carriers: [],
                      current_insurer: '',
                      other_insurer: '',
                      brokerage_insured_date: null,
                      owner_principal_date: null,
                    });
                  }
                }}
              >
                <HStack spacing={4}>
                  <Radio value="yes" colorScheme="blue" size="sm">
                    Yes
                  </Radio>
                  <Radio value="no" colorScheme="blue" size="sm">
                    No
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </GridItem>
        <GridItem>
          <FormControl>
            <Checkbox
              isChecked={personalInfo.lives_with_parents}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'personal_info',
                  'lives_with_parents',
                  e.target.checked,
                )
              }
              isDisabled={false}
            >
              Lives with Parents
            </Checkbox>
          </FormControl>
        </GridItem>
      </Grid>
    </>
  );
};

export default DriverPersonalInfo;
